import type { PfmeaRow } from "./types";

/**
 * Package #2.7 — AIAG-VDA Action Priority (AP) for PFMEA.
 *
 * Default table transcribed from the AIAG-VDA FMEA Handbook (1st ed., 2019),
 * PFMEA Action Priority table. Stored as reference data in policy setting
 * `pfmea.action_priority_table`; this constant is the fallback.
 *
 * matrix[severityBand][occurrenceBand] is a 4-letter string, one letter per
 * detection band in `detectionBands` order (D 7–10, 5–6, 2–4, 1).
 */
export type ActionPriority = "H" | "M" | "L";

export interface ActionPriorityTable {
  source: string;
  severityBands: [number, number][];
  occurrenceBands: [number, number][];
  detectionBands: [number, number][];
  matrix: string[][];
}

export const DEFAULT_AP_TABLE: ActionPriorityTable = {
  source: "AIAG-VDA FMEA Handbook (2019), PFMEA Action Priority table",
  severityBands: [
    [9, 10],
    [7, 8],
    [4, 6],
    [2, 3],
    [1, 1],
  ],
  occurrenceBands: [
    [8, 10],
    [6, 7],
    [4, 5],
    [2, 3],
    [1, 1],
  ],
  detectionBands: [
    [7, 10],
    [5, 6],
    [2, 4],
    [1, 1],
  ],
  matrix: [
    // S 9–10:  O 8–10, O 6–7, O 4–5, O 2–3, O 1
    ["HHHH", "HHHH", "HHHM", "HMLL", "LLLL"],
    // S 7–8
    ["HHHH", "HHHM", "HMMM", "MMLL", "LLLL"],
    // S 4–6
    ["HHMM", "MMML", "MLLL", "LLLL", "LLLL"],
    // S 2–3
    ["MMLL", "LLLL", "LLLL", "LLLL", "LLLL"],
    // S 1
    ["LLLL", "LLLL", "LLLL", "LLLL", "LLLL"],
  ],
};

let active: ActionPriorityTable = DEFAULT_AP_TABLE;

function isValidTable(t: unknown): t is ActionPriorityTable {
  const x = t as ActionPriorityTable;
  return (
    !!x &&
    Array.isArray(x.severityBands) &&
    Array.isArray(x.occurrenceBands) &&
    Array.isArray(x.detectionBands) &&
    Array.isArray(x.matrix) &&
    x.matrix.length === x.severityBands.length &&
    x.matrix.every(
      (r) =>
        Array.isArray(r) &&
        r.length === x.occurrenceBands.length &&
        r.every(
          (c) =>
            typeof c === "string" && c.length === x.detectionBands.length && /^[HML]+$/.test(c),
        ),
    )
  );
}

/** Adopt a customer-adjusted table from policy; invalid values keep the default. */
export function setActionPriorityTable(t: unknown) {
  active = isValidTable(t) ? t : DEFAULT_AP_TABLE;
}

const band = (bands: [number, number][], v: number) =>
  bands.findIndex(([lo, hi]) => v >= lo && v <= hi);

export function actionPriority(
  s: number,
  o: number,
  d: number,
  table: ActionPriorityTable = active,
): ActionPriority {
  const si = band(table.severityBands, s);
  const oi = band(table.occurrenceBands, o);
  const di = band(table.detectionBands, d);
  if (si < 0 || oi < 0 || di < 0) return "L";
  return (table.matrix[si]?.[oi]?.[di] as ActionPriority) ?? "L";
}

export const apBefore = (r: PfmeaRow) => actionPriority(r.sevBefore, r.occBefore, r.detBefore);
export const apAfter = (r: PfmeaRow) => actionPriority(r.sevAfter, r.occAfter, r.detAfter);

const RANK: Record<ActionPriority, number> = { L: 0, M: 1, H: 2 };
export const highestAp = (list: ActionPriority[]): ActionPriority =>
  list.reduce<ActionPriority>((a, b) => (RANK[b] > RANK[a] ? b : a), "L");

export const AP_LABEL: Record<ActionPriority, string> = { H: "High", M: "Medium", L: "Low" };
export const AP_NOTE =
  "Action Priority per AIAG-VDA FMEA Handbook (2019). RPN shown for legacy reference.";

export function recordApDelta(pfmea: PfmeaRow[]) {
  return { before: highestAp(pfmea.map(apBefore)), after: highestAp(pfmea.map(apAfter)) };
}
